import React, { createContext, useContext, useState } from 'react';

/**
 * TripContext - Manages trip-related state across the application
 * Used by Landing page and Chat view to share trip parameters
 */
const TripContext = createContext(null);

export const useTripContext = () => {
  const context = useContext(TripContext);
  if (!context) {
    throw new Error('useTripContext must be used within a TripProvider');
  }
  return context;
};

// Departure city - fixed to Singapore
export const DEPARTURE_CITY = { code: 'SIN', city: 'Singapore', country: 'Singapore' };

// Trip type options
export const TRIP_TYPES = {
  ROUND_TRIP: 'round-trip',
  ONE_WAY: 'one-way',
};

// Cabin class options
export const CABIN_CLASSES = {
  ECONOMY: 'Economy',
  PREMIUM_ECONOMY: 'Premium Economy',
  BUSINESS: 'Business',
  FIRST: 'First',
};

export const TripProvider = ({ children }) => {
  // Departure city (fixed: Singapore)
  const departureCity = DEPARTURE_CITY;
  
  // Destination (selected from search or cards)
  const [destination, setDestination] = useState(null);
  
  // Trip type (round trip or one way)
  const [tripType, setTripType] = useState(TRIP_TYPES.ROUND_TRIP);
  
  // Travel dates
  const [departureDate, setDepartureDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  
  // Passengers
  const [passengers, setPassengers] = useState({
    adults: 1,
    children: 0,
    infants: 0,
  });
  
  // Cabin class
  const [cabinClass, setCabinClass] = useState(CABIN_CLASSES.ECONOMY);
  
  // Checked bags
  const [checkedBags, setCheckedBags] = useState(0);
  
  // Current view state
  const [currentView, setCurrentView] = useState('landing'); // 'landing' | 'chat'
  
  // Initial search query (from search box or card click)
  const [initialQuery, setInitialQuery] = useState('');

  // Get total passenger count
  const getTotalPassengers = () => {
    return passengers.adults + passengers.children + passengers.infants;
  };

  // Format departure city display
  const getDepartureCityDisplay = () => {
    return `${departureCity.city} (${departureCity.code})`;
  };

  // Start chat with a query
  const startChatWithQuery = (query, dest = null) => {
    setInitialQuery(query);
    if (dest) {
      setDestination(dest);
    }
    setCurrentView('chat');
  };

  // Go back to landing
  const goToLanding = () => {
    setCurrentView('landing');
    setInitialQuery('');
  };

  // Reset trip state
  const resetTrip = () => {
    setDestination(null);
    setTripType(TRIP_TYPES.ROUND_TRIP);
    setDepartureDate(null);
    setReturnDate(null);
    setPassengers({ adults: 1, children: 0, infants: 0 });
    setCabinClass(CABIN_CLASSES.ECONOMY);
    setCheckedBags(0);
    setInitialQuery('');
  };

  // Build context message for agent
  const buildContextMessage = () => {
    let context = `Departing from ${getDepartureCityDisplay()}`;
    
    if (destination) {
      context += ` to ${destination}`;
    }
    
    context += `. ${tripType === TRIP_TYPES.ROUND_TRIP ? 'Round trip' : 'One way'}`;
    context += `, ${getTotalPassengers()} passenger(s)`;
    context += `, ${cabinClass} class`;
    
    if (checkedBags > 0) {
      context += `, ${checkedBags} checked bag(s)`;
    }
    
    return context;
  };

  return (
    <TripContext.Provider
      value={{
        // State
        departureCity,
        destination,
        tripType,
        departureDate,
        returnDate,
        passengers,
        cabinClass,
        checkedBags,
        currentView,
        initialQuery,
        
        // Setters
        setDestination,
        setTripType,
        setDepartureDate,
        setReturnDate,
        setPassengers,
        setCabinClass,
        setCheckedBags,
        
        // Helpers
        getTotalPassengers,
        getDepartureCityDisplay,
        startChatWithQuery,
        goToLanding,
        resetTrip,
        buildContextMessage,
        
        // Constants
        DEPARTURE_CITY,
        TRIP_TYPES,
        CABIN_CLASSES,
      }}
    >
      {children}
    </TripContext.Provider>
  );
};

export default TripContext;
